var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef } from "react";
import { Box, useMediaQuery } from "@mui/material";
import PageHeader from "@ui/components/header/PageHeader";
import { PageContainer, PageContent } from "@ui/components/wraper";
import { SIconButton } from "@ui/components/buttons/SIconButton";
import { GridFilters } from "@ui/components/grid/GridFilters";
import { SecondaryGrid } from "@ui/components/grid/SecondaryGrid";
import { Icon } from "@iconify/react";
import { colCenter } from "@ui/components/style/style";
import { useNavigate } from "react-router";
import { SButton } from "@ui/components/buttons";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAllAlarmCauses, deleteAlarmCause, } from "@reducers/alarm-causes/thunk/alarm-causes-thunk";
import { setFilters, setGlobalSearch, setPageNumber, setPageSize, } from "@reducers/alarm-causes/slice/alarm-causes-slice";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { AlarmCauseCard } from "./components/AlarmCauseCard";
export const ManageAlarm = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["common"]);
    const { alarmCauses, loading, paginationInfo, filters, currentPageNumber, currentPageSize, } = useAppSelector((state) => state.alarmCauses);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [alarmCauseToDelete, setAlarmCauseToDelete] = React.useState(null);
    // Track previous values to prevent duplicate calls
    const prevValuesRef = useRef(null);
    useEffect(() => {
        const currentValues = {
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            definition: filters.definition,
            globalSearch: filters.globalSearch,
        };
        // Check if values have actually changed
        const prevValues = prevValuesRef.current;
        if (prevValues &&
            prevValues.pageNumber === currentValues.pageNumber &&
            prevValues.pageSize === currentValues.pageSize &&
            prevValues.definition === currentValues.definition &&
            prevValues.globalSearch === currentValues.globalSearch) {
            return; // Skip if nothing changed
        }
        prevValuesRef.current = currentValues;
        dispatch(getAllAlarmCauses({
            filters: {
                definition: filters.definition,
                globalSearch: filters.globalSearch,
            },
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
        }));
    }, [
        dispatch,
        currentPageNumber,
        currentPageSize,
        filters.definition,
        filters.globalSearch,
    ]);
    const handleFilterChange = (newFilters) => {
        if (newFilters.definition !== undefined) {
            dispatch(setFilters({ definition: newFilters.definition }));
        }
    };
    const handleSearchChange = (e) => {
        dispatch(setGlobalSearch(e.target.value));
    };
    const handlePageChange = (page) => {
        dispatch(setPageNumber(page + 1));
    };
    const handlePageSizeChange = (pageSize) => {
        dispatch(setPageSize(pageSize));
        dispatch(setPageNumber(1));
    };
    const handleDelete = (id) => {
        setAlarmCauseToDelete(id);
        setDeleteDialogOpen(true);
    };
    const handleConfirmDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (alarmCauseToDelete) {
            try {
                yield dispatch(deleteAlarmCause(alarmCauseToDelete)).unwrap();
                toast.success(t("common:toasts.alarmCauseDeletedSuccessfully"));
                dispatch(getAllAlarmCauses({
                    filters: {
                        definition: filters.definition,
                        globalSearch: filters.globalSearch,
                    },
                    pageNumber: currentPageNumber,
                    pageSize: currentPageSize,
                }));
                setDeleteDialogOpen(false);
                setAlarmCauseToDelete(null);
            }
            catch (error) {
                toast.error(error || t("common:toasts.failedToDeleteAlarmCause"));
            }
        }
    });
    const columns = [
        {
            field: "definition",
            headerName: t("common:grid.definition"),
            flex: 2,
            filterable: true,
            filterType: "text",
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            width: 160,
            headerAlign: "center",
            sortable: false,
            renderCell: (params) => (React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { height: "100%", gap: 1 }) },
                React.createElement(SIconButton, { colorVariant: "error", title: "Delete", size: "small", onClick: () => handleDelete(params.row.id) },
                    React.createElement(Icon, { icon: "mingcute:close-line" })),
                React.createElement(SButton, { variant: "soft", color: "primary", size: "medium", onClick: () => navigate(`/alarms/manage/${params.row.id}/edit`) }, t("common:actions.edit")))),
            type: "actions",
        },
    ];
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.alarmCauses"), showBackButton: true, topActions: 
            // isMobile ? null : (
            React.createElement(SButton, { variant: "contained", size: "large", onClick: () => navigate("/alarms/manage/new") }, t("common:actions.addAlarmCause")) }),
        React.createElement(PageContent, null,
            React.createElement(Box, { sx: { mb: 1 } },
                React.createElement(GridFilters, { columns: columns, filters: filters, onChange: handleFilterChange, searchProp: filters.globalSearch || "", handleSearchChange: handleSearchChange, title: `${(paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) || 0} ${t("common:pages.alarmCauses")}`, loading: loading })),
            React.createElement(SecondaryGrid, { rows: alarmCauses || [], columns: columns, loading: loading, paginationModel: {
                    page: (currentPageNumber || 1) - 1,
                    pageSize: currentPageSize || 10,
                }, onPaginationModelChange: (model) => {
                    handlePageChange(model.page);
                    if (model.pageSize !== currentPageSize) {
                        handlePageSizeChange(model.pageSize);
                    }
                }, rowCount: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) || 0, pageSizeOptions: [25, 50], paginationMode: "server", renderCard: (row) => (React.createElement(AlarmCauseCard, { row: row, onDelete: handleDelete, onEdit: (id) => navigate(`/alarms/manage/${id}/edit`) })) }),
            React.createElement(ConfirmDialog, { isOpen: deleteDialogOpen, onClose: () => {
                    setDeleteDialogOpen(false);
                    setAlarmCauseToDelete(null);
                }, onConfirm: handleConfirmDelete, actionText: t("common:actions.delete"), screenName: t("common:labels.alarmCause"), customMessage: "Are you sure you want to delete this alarm cause? This action cannot be undone.", variant: "delete" }))));
};
